import React, { useState } from "react";
import HUDFrame from "../components/HUDFrame";
import { Mail, Globe, MessagesSquare, CheckCircle2, Loader2 } from "lucide-react";
import { submitContact } from "../lib/api";

const initial = { name: "", email: "", company: "", phone: "", subject: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const onChange = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await submitContact(form);
      setDone(true);
      setForm(initial);
    } catch (err) {
      setError(
        err?.response?.data?.detail?.[0]?.msg ||
          err?.response?.data?.detail ||
          "Unable to submit. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative z-10 pt-32 pb-20">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid lg:grid-cols-[1fr_1.2fr] gap-10">
        <div>
          <div className="hud-pill">GET IN TOUCH</div>
          <h1 className="mt-6 text-4xl sm:text-5xl font-display font-black leading-[0.95]">
            <span className="text-white">Let's talk</span>
            <br />
            <span className="text-green glow">intelligence.</span>
          </h1>
          <p className="mt-5 text-lg text-[color:var(--ss-muted)] max-w-lg">
            Whether you're evaluating StrainScan Pro for a single location or a
            multi-state operation, our enterprise team is ready to help you plan,
            pilot, and deploy.
          </p>

          <div className="mt-10 space-y-5">
            <HUDFrame padding="p-5">
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 border border-[rgba(157,252,4,0.4)] grid place-items-center text-[color:var(--ss-green)]">
                  <Mail size={20} />
                </div>
                <div>
                  <div className="text-[10px] tracking-[0.24em] font-display text-[color:var(--ss-green)]">EMAIL</div>
                  <a href="mailto:info@strainscanpro.com" className="text-white hover:text-[color:var(--ss-green)]" data-testid="contact-email-link">
                    info@strainscanpro.com
                  </a>
                </div>
              </div>
            </HUDFrame>
            <HUDFrame padding="p-5">
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 border border-[rgba(157,252,4,0.4)] grid place-items-center text-[color:var(--ss-green)]">
                  <Globe size={20} />
                </div>
                <div>
                  <div className="text-[10px] tracking-[0.24em] font-display text-[color:var(--ss-green)]">WEB</div>
                  <div className="text-white">www.strainscanpro.com</div>
                </div>
              </div>
            </HUDFrame>
            <HUDFrame padding="p-5">
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 border border-[rgba(157,252,4,0.4)] grid place-items-center text-[color:var(--ss-green)]">
                  <MessagesSquare size={20} />
                </div>
                <div>
                  <div className="text-[10px] tracking-[0.24em] font-display text-[color:var(--ss-green)]">RESPONSE TIME</div>
                  <div className="text-white">Within 24 business hours</div>
                </div>
              </div>
            </HUDFrame>
          </div>
        </div>

        <HUDFrame padding="p-7 md:p-10">
          {done ? (
            <div className="text-center py-10" data-testid="contact-success">
              <CheckCircle2 size={64} className="mx-auto text-[color:var(--ss-green)]" />
              <h2 className="mt-5 text-2xl md:text-3xl font-display glow">Message sent</h2>
              <p className="mt-3 text-[color:var(--ss-muted)]">
                Thanks for reaching out — we'll reply shortly.
              </p>
              <button type="button" className="btn-ghost mt-6" onClick={() => setDone(false)}>
                Send another message
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="contact-form">
              <div>
                <label className="ss-label">Name</label>
                <input required className="ss-input" value={form.name} onChange={onChange("name")} data-testid="contact-name" />
              </div>
              <div>
                <label className="ss-label">Email</label>
                <input required type="email" className="ss-input" value={form.email} onChange={onChange("email")} data-testid="contact-email" />
              </div>
              <div>
                <label className="ss-label">Company</label>
                <input className="ss-input" value={form.company} onChange={onChange("company")} data-testid="contact-company" />
              </div>
              <div>
                <label className="ss-label">Phone</label>
                <input className="ss-input" value={form.phone} onChange={onChange("phone")} data-testid="contact-phone" />
              </div>
              <div className="md:col-span-2">
                <label className="ss-label">Subject</label>
                <input className="ss-input" value={form.subject} onChange={onChange("subject")} data-testid="contact-subject" />
              </div>
              <div className="md:col-span-2">
                <label className="ss-label">Message</label>
                <textarea required rows={6} className="ss-textarea" value={form.message} onChange={onChange("message")} data-testid="contact-message" />
              </div>

              {error && (
                <div className="md:col-span-2 text-red-400 text-sm" data-testid="contact-error">
                  {error}
                </div>
              )}

              <div className="md:col-span-2 flex justify-end pt-2">
                <button type="submit" className="btn-primary" disabled={loading} data-testid="contact-submit">
                  {loading ? <Loader2 size={16} className="animate-spin" /> : null}
                  {loading ? "Sending…" : "Send Message"}
                </button>
              </div>
            </form>
          )}
        </HUDFrame>
      </div>
    </div>
  );
}
